import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom'
import TradingCard, { CardData } from './TradingCard'
import { useTemplateProjections, projectionPillStyle, TemplateProjection } from '@/hooks/useCardProjection'

interface PackOpeningModalProps {
  packName: string
  cards: CardData[]
  onClose: () => void

  // ── Selection mode (pack revamp) ──
  // When pendingId is supplied, the modal switches into reveal-then-select
  // mode: after the reveal animation, the user clicks N cards to keep
  // (cardsKept) and the rest are discarded.  onConfirmSelection commits
  // the choice via the API; the parent closes the modal afterwards.
  // When pendingId is omitted (starter packs / legacy free grants), all
  // revealed cards are kept and the modal behaves as before.
  pendingId?: number
  cardsKept?: number
  onConfirmSelection?: (keptIndices: number[]) => Promise<void> | void
}

const EDITION_COLORS: Record<string, string> = {
  metallic: '#94a3b8',
  holographic: '#c4b5fd',
  prismatic: '#f9a8d4',
  diamond: '#a5f3fc',
}

const EDITION_RANK: Record<string, number> = {
  metallic: 0,
  holographic: 1,
  prismatic: 2,
  diamond: 3,
}

// Delay before revealing each edition (rarer = longer anticipation)
const EDITION_DELAY: Record<string, number> = {
  metallic: 250,
  holographic: 300,
  prismatic: 400,
  diamond: 600,
}

// Glow burst config per edition
const EDITION_GLOW: Record<string, { color: string; size: number; duration: number }> = {
  metallic: { color: 'rgba(148,163,184,0.3)', size: 60, duration: 300 },
  holographic: { color: 'rgba(167,139,250,0.4)', size: 80, duration: 400 },
  prismatic: { color: 'rgba(244,114,182,0.6)', size: 140, duration: 600 },
  diamond: { color: 'rgba(103,232,249,0.7)', size: 200, duration: 800 },
}

// ─── Particle burst for prismatic/diamond ─────────────────────────────────

interface Particle {
  id: number
  x: number
  y: number
  angle: number
  speed: number
  size: number
  color: string
  life: number
}

const ParticleBurst: React.FC<{ edition: string; active: boolean }> = ({ edition, active }) => {
  const [particles, setParticles] = useState<Particle[]>([])
  const frameRef = useRef<number>()
  const startRef = useRef<number>(0)

  useEffect(() => {
    if (!active) return
    if (edition !== 'prismatic' && edition !== 'diamond') return

    const count = edition === 'diamond' ? 24 : 14
    const colors = edition === 'diamond'
      ? ['#a5f3fc', '#ffffff', '#67e8f9', '#22d3ee', '#ecfeff']
      : ['#f9a8d4', '#fce7f3', '#f472b6', '#ffffff', '#c084fc']

    const newParticles: Particle[] = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 0,
      y: 0,
      angle: (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5,
      speed: 1.5 + Math.random() * 2.5,
      size: 2 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 1,
    }))
    setParticles(newParticles)
    startRef.current = Date.now()

    const duration = edition === 'diamond' ? 900 : 700
    const animate = () => {
      const elapsed = Date.now() - startRef.current
      const progress = Math.min(1, elapsed / duration)
      setParticles(prev => prev.map(p => ({
        ...p,
        x: Math.cos(p.angle) * p.speed * progress * 80,
        y: Math.sin(p.angle) * p.speed * progress * 80 - progress * 20,
        life: 1 - progress,
      })))
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate)
      }
    }
    frameRef.current = requestAnimationFrame(animate)
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current) }
  }, [active, edition])

  if (particles.length === 0) return null

  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: '50%',
            backgroundColor: p.color,
            transform: `translate(${p.x}px, ${p.y}px)`,
            opacity: p.life * 0.9,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
          }}
        />
      ))}
    </div>
  )
}

// ─── Screen flash for diamond ───────────────────────────────────────────────

const ScreenFlash: React.FC<{ active: boolean }> = ({ active }) => {
  const [phase, setPhase] = useState<'idle' | 'flash' | 'fade'>('idle')

  useEffect(() => {
    if (!active) return
    setPhase('flash')
    const t1 = setTimeout(() => setPhase('fade'), 50)
    const t2 = setTimeout(() => setPhase('idle'), 450)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [active])

  if (phase === 'idle') return null

  return (
    <div style={{
      position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10000,
      backgroundColor: '#a5f3fc',
      opacity: phase === 'flash' ? 0.35 : 0,
      transition: phase === 'fade' ? 'opacity 0.4s ease-out' : 'none',
    }} />
  )
}

// ─── Projection Pill (inline, kept self-contained for the modal) ──────────

const ProjectionPillInline: React.FC<{ proj: TemplateProjection }> = ({ proj }) => {
  const style = projectionPillStyle(proj)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center',
          fontSize: '12px', fontWeight: 700,
          color: style.color, backgroundColor: style.bg,
          padding: '4px 10px',
          border: `1px solid ${style.color}55`,
          fontVariantNumeric: 'tabular-nums' as const,
          whiteSpace: 'nowrap' as const,
        }}
      >
        {style.label}
      </span>
      {style.ceiling && (
        <span style={{
          fontSize: '10px', color: style.color, opacity: 0.8,
          fontVariantNumeric: 'tabular-nums' as const,
          whiteSpace: 'nowrap' as const,
        }}>
          {style.ceiling}
        </span>
      )}
    </div>
  )
}


// ─── Main Modal ────────────────────────────────────────────────────────────

const PackOpeningModal: React.FC<PackOpeningModalProps> = ({
  packName, cards, onClose,
  pendingId, cardsKept, onConfirmSelection,
}) => {
  const [revealedCount, setRevealedCount] = useState(0)
  const [allRevealed, setAllRevealed] = useState(false)
  const [glowStates, setGlowStates] = useState<Map<number, boolean>>(new Map())
  const [diamondFlash, setDiamondFlash] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [confirming, setConfirming] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  // Selection mode: pendingId set + cardsKept < cards.length means the user
  // must pick which to keep before the modal closes.
  const isSelectionMode =
    pendingId != null && cardsKept != null && cardsKept < cards.length

  // Fetch projections for every revealed card. Shown under each card
  // whether the user is picking N of M (selection mode) or auto-keeping
  // everything (starter / free packs) — projections are useful info
  // either way, not just for comparison.
  const templateIds = useMemo(
    () => cards.map(c => c.templateId).filter(Boolean),
    [cards],
  )
  const { byTemplateId: projByTemplateId } = useTemplateProjections(templateIds)

  // Sort cards: base first, diamond last (rarest revealed last for drama)
  const sortedCards = [...cards].sort(
    (a, b) => (EDITION_RANK[a.edition] ?? 0) - (EDITION_RANK[b.edition] ?? 0)
  )

  const toggleSelect = (idx: number) => {
    if (!isSelectionMode || !allRevealed || confirming) return
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(idx)) {
        next.delete(idx)
      } else if (next.size < (cardsKept || 0)) {
        next.add(idx)
      }
      return next
    })
  }

  const handleConfirm = async () => {
    if (!onConfirmSelection || selected.size !== (cardsKept || 0)) return
    setConfirming(true)
    try {
      // `selected` holds indices into the sorted-for-reveal view, but the
      // backend's revealed_template_ids is in original draw order. Translate
      // by object identity (sortedCards shares refs with cards) before sending.
      const originalIndices = Array.from(selected)
        .map(si => cards.indexOf(sortedCards[si]))
        .filter(i => i >= 0)
        .sort((a, b) => a - b)
      await onConfirmSelection(originalIndices)
    } finally {
      setConfirming(false)
    }
  }

  const triggerGlow = useCallback((index: number) => {
    setGlowStates(prev => new Map(prev).set(index, true))
    setTimeout(() => {
      setGlowStates(prev => new Map(prev).set(index, false))
    }, (EDITION_GLOW[sortedCards[index]?.edition]?.duration ?? 300) + 200)
  }, [sortedCards])

  // Auto-reveal cards one at a time with edition-appropriate pacing
  useEffect(() => {
    if (revealedCount >= sortedCards.length) {
      setAllRevealed(true)
      return
    }
    const nextEdition = sortedCards[revealedCount]?.edition ?? 'metallic'
    const delay = EDITION_DELAY[nextEdition] ?? 350

    // Diamond gets a pre-reveal flash
    if (nextEdition === 'diamond') {
      timerRef.current = setTimeout(() => {
        setDiamondFlash(true)
        setTimeout(() => {
          setDiamondFlash(false)
          triggerGlow(revealedCount)
          setRevealedCount(prev => prev + 1)
        }, 250)
      }, delay)
    } else {
      timerRef.current = setTimeout(() => {
        triggerGlow(revealedCount)
        setRevealedCount(prev => prev + 1)
      }, delay)
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [revealedCount, sortedCards, triggerGlow])

  const revealAll = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setRevealedCount(sortedCards.length)
    setAllRevealed(true)
  }

  // Backdrop click behavior depends on mode:
  //  - reveal animating: skip ahead to all-revealed
  //  - selection mode: ignore (require Confirm)
  //  - free/legacy mode: close
  const handleBackdropClick = () => {
    if (!allRevealed) {
      revealAll()
      return
    }
    if (isSelectionMode) return
    onClose()
  }

  const keepCount = cardsKept || 0
  const canConfirm = isSelectionMode && allRevealed && selected.size === keepCount && !confirming

  return ReactDOM.createPortal(
    <>
      <ScreenFlash active={diamondFlash} />
      <div
        onClick={handleBackdropClick}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.85)',
          // Scroll container. Content (5 revealed cards stacked + the
          // counter + the confirm button) can be taller than a phone
          // viewport. Without scroll, users on mobile never see the
          // bottom cards or the confirm button — they effectively
          // burn a pack they can't open. `flex` with `justifyContent:
          // 'center'` would prevent scrolling to the top of overflowing
          // content, so use a simple block + padding instead and let
          // the content stack from the top.
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '24px 12px 32px',
          fontFamily: 'pressStart',
          cursor: !allRevealed || (allRevealed && !isSelectionMode) ? 'pointer' : 'default',
        }}
      >
        {/* Prevent clicks on cards from closing */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            cursor: 'default',
            // Center the content column horizontally. minHeight pads the
            // short-content case (mid-reveal) so the modal still feels
            // like a centered overlay rather than a top-stuck panel.
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 56px)',
            width: '100%',
          }}
        >
          {/* Pack name + (selection counter when applicable) */}
          <div style={{
            fontSize: '18px', fontWeight: '700', color: '#e2e8f0',
            textAlign: 'center', marginBottom: isSelectionMode && allRevealed ? '8px' : '24px',
          }}>
            {packName}
          </div>
          {isSelectionMode && allRevealed && (
            <div style={{
              fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginBottom: '20px',
            }}>
              Pick {keepCount} of {cards.length} to keep:{' '}
              <span style={{ color: selected.size === keepCount ? '#22c55e' : '#eab308', fontWeight: 700 }}>
                {selected.size}/{keepCount} selected
              </span>
            </div>
          )}

          {/* Cards row. Top-align so cards with extra info below them (projection
              pill, "You own N" badge) don't float upward relative to ones without. */}
          <div style={{
            display: 'flex', gap: '16px', flexWrap: 'wrap',
            justifyContent: 'center', alignItems: 'flex-start',
          }}>
            {sortedCards.map((card, i) => {
              const isRevealed = i < revealedCount
              const edition = card.edition
              const glow = EDITION_GLOW[edition] ?? EDITION_GLOW.metallic
              const isGlowing = glowStates.get(i) ?? false
              const isSelected = selected.has(i)
              const selectable = isSelectionMode && allRevealed
              const canStillSelect = !isSelected && selected.size < keepCount

              return (
                <div
                  key={card.id ?? `card_${i}`}
                  style={{
                    position: 'relative',
                    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s, filter 0.2s',
                    transform: isRevealed
                      ? (isGlowing && edition !== 'metallic'
                          ? 'scale(1.06)'
                          : isSelected ? 'translateY(-8px) scale(1.02)' : 'scale(1)')
                      : 'rotateY(90deg) scale(0.8)',
                    opacity: isRevealed
                      ? (selectable && !isSelected && !canStillSelect ? 0.45 : 1)
                      : 0,
                    cursor: selectable ? (isSelected || canStillSelect ? 'pointer' : 'not-allowed') : 'default',
                    filter: selectable && !isSelected && !canStillSelect ? 'grayscale(0.4)' : 'none',
                  }}
                >
                  {/* Glow burst behind card */}
                  <div style={{
                    position: 'absolute',
                    inset: '-30px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${glow.color} 0%, transparent 70%)`,
                    opacity: isGlowing ? 1 : 0,
                    transition: `opacity ${glow.duration}ms ease-out`,
                    pointerEvents: 'none',
                    filter: `blur(${glow.size / 4}px)`,
                    zIndex: -1,
                  }} />

                  {/* Particle burst */}
                  <ParticleBurst edition={edition} active={isGlowing} />

                  {/* Selection ring */}
                  {selectable && isSelected && (
                    <div style={{
                      position: 'absolute', inset: '-8px',
                      border: '3px solid #22c55e',
                      boxShadow: '0 0 20px rgba(34,197,94,0.5)',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }} />
                  )}
                  {selectable && isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px', right: '-12px',
                      width: 28, height: 28, borderRadius: '50%',
                      backgroundColor: '#22c55e',
                      color: '#0f172a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', fontWeight: 700,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                      zIndex: 3,
                    }}>
                      ✓
                    </div>
                  )}

                  {isRevealed ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <TradingCard
                        card={card}
                        size="md"
                        // In selection mode, hijack the card's click to toggle
                        // the keep selection. TradingCard otherwise flips on
                        // click — we don't want both behaviors stacked.
                        onClick={selectable ? () => toggleSelect(i) : undefined}
                      />
                      {projByTemplateId.get(card.templateId) && (
                        <ProjectionPillInline proj={projByTemplateId.get(card.templateId)!} />
                      )}
                      {/* Vault-bound cards get Showcase points instead of a
                          projection. They can't be fielded, so points are the
                          only thing that says which one to keep. */}
                      {(card as any).showcase && (
                        <span style={{
                          fontSize: '11px', fontWeight: 700, color: '#fbbf24',
                          backgroundColor: 'rgba(251,191,36,0.12)',
                          padding: '2px 7px',
                          fontVariantNumeric: 'tabular-nums',
                        }}>{Math.round((card as any).showcase.points)} pts</span>
                      )}
                      {/* No-effect (standard) cards can't be upgraded, so the owned count is moot */}
                      {card.edition !== 'base' && (card.ownedEffectCount ?? 0) > 0 && (
                        <span
                          style={{
                            fontSize: '10px', fontFamily: 'pressStart', fontWeight: 600,
                            color: '#fbbf24', padding: '2px 6px',
                            border: '1px solid rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.10)',
                          }}
                        >
                          You own {card.ownedEffectCount}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      width: 180, height: 260,
                      backgroundColor: '#1e293b', border: '2px solid #334155',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '28px', color: '#334155' }}>?</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Rarity summary */}
          {allRevealed && !isSelectionMode && (
            <div style={{
              marginTop: '20px', textAlign: 'center',
              display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
            }}>
              {sortedCards.map((card, i) => (
                <span key={i} style={{
                  fontSize: '10px', fontWeight: '600',
                  color: EDITION_COLORS[card.edition] || '#94a3b8',
                  textTransform: 'uppercase',
                }}>
                  {card.edition}
                </span>
              ))}
            </div>
          )}

          {/* Bottom bar: confirm button (selection mode) or close button (free packs) */}
          {isSelectionMode && allRevealed ? (
            <div style={{
              marginTop: '28px', display: 'flex', justifyContent: 'center', gap: '12px',
            }}>
              <button
                onClick={handleConfirm}
                disabled={!canConfirm}
                style={{
                  padding: '10px 28px',
                  border: 'none',
                  backgroundColor: canConfirm ? '#22c55e' : '#1e293b',
                  color: canConfirm ? '#0f172a' : '#64748b',
                  fontSize: '13px', fontWeight: 700,
                  cursor: canConfirm ? 'pointer' : 'not-allowed',
                  fontFamily: 'pressStart',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
              >
                {confirming
                  ? 'Confirming...'
                  : selected.size < keepCount
                    ? `Pick ${keepCount - selected.size} more`
                    : 'Confirm Selection'}
              </button>
            </div>
          ) : allRevealed ? (
            // Explicit Close button for free-grant / starter flows. The
            // inner content stops click propagation (so card clicks don't
            // close the modal during selection mode), which also blocked
            // the old "click anywhere to close" backdrop dismiss — cards
            // filled the screen and users had no way out.
            <div style={{
              marginTop: '28px', display: 'flex', justifyContent: 'center',
            }}>
              <button
                onClick={onClose}
                style={{
                  padding: '10px 28px',
                  border: 'none',
                  backgroundColor: '#22c55e',
                  color: '#0f172a',
                  fontSize: '13px', fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'pressStart',
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <div style={{
              marginTop: '24px', fontSize: '11px', color: '#64748b',
              textAlign: 'center',
            }}>
              Click to reveal all
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  )
}

export default PackOpeningModal
